import { cloneDeep } from "lodash";
import moment from "moment";
import i18nService from "@/services/i18n";
import { Facade, TableDataAndJournalParams } from "../../../common/Facade";
import { FilterOptions } from "../../../common/types/roots/FilterOptions";
import { FilterSettings } from "../../../common/types/roots/FilterSettings";
import { FilterSettingsForm } from "../../../common/types/roots/FilterSettingsForm";
import { Option } from "../../../common/types/Option";
import { BudgetType, MutuallyRedeemingLevel, Segmentation } from "../../../common/types/roots/fields";

import { Draggable } from "../../../common/types/Draggable";
import { reconPfKfFilterOptions, reconPfKfExpenseClassificators, reconPfKfSegmentationOptions } from "../report-recon-pf-kf";
import { ReconPfKfFilterOptions } from "../options/ReconPfKfFilterOptions";
import { ReconPfKfFilterSettings } from "../settings/ReconPfKfFilterSettings";
import { ReconPfKfFilterSettingsForm } from "../settings-form/ReconPfKfFilterSettingsForm";
import { ReportReconPfKfTableDataRequest } from "../ReportReconPfKfTableDataRequest";

export class ReconPfKfFacade implements Facade {

    getFilterOptions(): FilterOptions {
        return cloneDeep(reconPfKfFilterOptions)
    }

    settingsToForm(settings: FilterSettings, options: FilterOptions): FilterSettingsForm {
        if (settings.reportName !== 'RECON-PF-KF') {
            throw new Error('error_type_filter_settings')
        }
        if (options.key !== 'RECON-PF-KF') {
            throw new Error('error_type_filter_options')
        }

        const s = settings as ReconPfKfFilterSettings
        const o = options as ReconPfKfFilterOptions

        let reportName = o.reportNames.find(it => it.value === s.reportName)
        if (!reportName) throw new Error('error_not_found_report_name')
        reportName = { ...reportName }

        let reportType = o.reportTypes.find(it => it.value === s.reportType)!
        if (!reportType) throw new Error('error_not_found_report_type')
        reportType = { ...reportType }

        const classificationType = { value: 'EXPENSES' as const, label: 'Расходы' }

        let dataSource = o.dataSources.find(it => it.value === s.dataSource)
        if (!dataSource) throw new Error('error_not_found_data_source')
        dataSource = { ...dataSource }

        let periodicity = o.periodicities.find(it => it.value === s.periodicity)
        if (!periodicity) throw new Error('error_not_found_periodicity')
        periodicity = { ...periodicity }

        const regionOption = o.regions.find(it => it.value === s.region) ?? null

        const budgetTypes: Option<BudgetType>[] = s.budgetTypes.map(bt => {
            const opt = o.budgetTypes.find(it => it.value === bt)!
            return { ...opt }
        })

        let typePF = o.typePFs.find(it => it.value === s.typePF) ?? o.typePFs[0]
        typePF = { ...typePF }

        let typePFComponent = o.typePFComponent.find(it => it.value === s.typePFComponent)
        if (!typePFComponent) throw new Error('error_not_found_type_pf_component')
        typePFComponent = { ...typePFComponent }

        const segmentations: Draggable<Segmentation>[] = cloneDeep(reconPfKfSegmentationOptions).map(seg => ({
            ...seg,
            active: s.segmentations.find(it => it.value === seg.value)?.active ?? seg.active
        }))

        const guOptions: Option<string>[] = s.gu.map(g => {
            const opt = o.gu.find(it => it.value === g)
            return opt ? { ...opt } : { value: g, label: g }
        })

        let mutuallyRedeeming = o.mutuallyRedeeming.find(it => it.value === s.mutuallyRedeeming)
        if (!mutuallyRedeeming) throw new Error('error_not_found_mutually_redeeming_option')
        mutuallyRedeeming = { ...mutuallyRedeeming }

        const mutuallyRedeemingLevels = s.mutuallyRedeemingLevels.map(level => {
            const opt = o.mutuallyRedeemingLevels.find(it => it.value === level)!
            return { ...opt }
        })

        const statuses = cloneDeep(o.statuses.filter(it => s.statuses.includes(it.value)))

        let measureUnit = o.measureUnits.find(it => it.value === s.measureUnit)
        if (!measureUnit) throw new Error('error_not_found_measure_unit')
        measureUnit = { ...measureUnit }

        const monthOption = s.month != null
            ? (o.months.find(it => it.value === s.month) ?? null)
            : null

        const form: ReconPfKfFilterSettingsForm = {
            key: 'RECON-PF-KF',
            reportName,
            reportType,
            classificationType,
            dataSource,
            periodicity,
            date: s.date ? new Date(s.date) : null,
            year: s.year,
            month: monthOption,
            region: regionOption,
            budgetTypes,
            typePF,
            typePFComponent,
            segmentations,
            gu: guOptions,
            mutuallyRedeeming,
            mutuallyRedeemingLevels,
            statuses,
            measureUnit,
            roundUp: s.roundUpTo != null,
            roundUpTo: s.roundUpTo,
            onlyDiscrepancies: s.onlyDiscrepancies,
            onlyLoaded304: s.onlyLoaded304,
            includeEarlierLoaded304: s.includeEarlierLoaded304,
            expenses: cloneDeep(s.expenses)
        }

        return form
    }

    formToSettings(form: FilterSettingsForm): FilterSettings {
        if (form.key !== 'RECON-PF-KF') {
            throw new Error('error_type_filter_settingsForm')
        }
        const f = form as ReconPfKfFilterSettingsForm

        if (!f.region) throw new Error('error_input_region')
        if (f.measureUnit == null) throw new Error('error_input_measure_unit')
        if (f.mutuallyRedeeming == null) throw new Error('error_input_mutually_redeeming')
        if (f.budgetTypes.length === 0) throw new Error('error_select_budget')
        if (f.periodicity.value === 'DAY' && !f.date) throw new Error('error_input_date')
        if (f.periodicity.value === 'MONTH' && !f.month) throw new Error('error_input_month')

        const roundUpTo = f.roundUp ? (f.roundUpTo ?? null) : null

        let dateStr = ''
        if (f.periodicity.value === 'DAY' && f.date) {
            dateStr = moment(f.date).format('YYYY-MM-DD')
        } else if (f.periodicity.value === 'MONTH' && f.month) {
            dateStr = `${f.year}-${String(f.month.value).padStart(2, '0')}-01`
        }

        const settings: ReconPfKfFilterSettings = {
            reportName: 'RECON-PF-KF',
            reportType: f.reportType.value,
            classificationType: 'EXPENSES',
            dataSource: f.dataSource.value,
            periodicity: f.periodicity.value,
            date: dateStr,
            year: f.year,
            month: f.month?.value ?? null,
            region: f.region.value,
            budgetTypes: f.budgetTypes.map(it => it.value),
            typePF: f.typePF.value,
            typePFComponent: f.typePFComponent.value,
            segmentations: cloneDeep(f.segmentations),
            gu: f.gu.map(it => it.value),
            mutuallyRedeeming: f.mutuallyRedeeming.value,
            mutuallyRedeemingLevels: f.mutuallyRedeemingLevels.map(it => it.value),
            statuses: f.statuses.map(it => it.value),
            measureUnit: f.measureUnit.value,
            roundUpTo,
            onlyDiscrepancies: f.onlyDiscrepancies,
            onlyLoaded304: f.onlyLoaded304,
            includeEarlierLoaded304: f.includeEarlierLoaded304,
            expenses: cloneDeep(f.expenses)
        }

        return settings
    }

    getEmptyForm(): FilterSettingsForm {
        const options = cloneDeep(reconPfKfFilterOptions) as ReconPfKfFilterOptions

        const form: ReconPfKfFilterSettingsForm = {
            key: 'RECON-PF-KF',
            reportName: options.reportNames.find(it => it.value === 'RECON-PF-KF')!,
            reportType: options.reportTypes.find(it => it.value === 'REGULATED')!,
            classificationType: { value: 'EXPENSES', label: 'Расходы' },
            dataSource: options.dataSources.find(it => it.value === 'LOADER')!,
            periodicity: options.periodicities.find(it => it.value === 'DAY')!,
            date: null,
            year: new Date().getFullYear(),
            month: null,
            region: null,
            budgetTypes: [],
            typePF: options.typePFs[0],
            typePFComponent: options.typePFComponent.find(it => it.value === 'OBLIGATIONS')!,
            segmentations: cloneDeep(reconPfKfSegmentationOptions),
            gu: [],
            mutuallyRedeeming: options.mutuallyRedeeming.find(it => it.value === 'WITHOUT')!,
            mutuallyRedeemingLevels: [],
            statuses: cloneDeep(options.statuses.filter(it => it.value === '39')),
            measureUnit: options.measureUnits.find(it => it.value === 'THOUSAND')!,
            roundUp: false,
            roundUpTo: null,
            onlyDiscrepancies: true,
            onlyLoaded304: true,
            includeEarlierLoaded304: false,
            expenses: cloneDeep(reconPfKfExpenseClassificators)
        }

        return form
    }

    async constructReport(
        form: FilterSettingsForm,
        templateName: string,
        excelName: string | null,
        userId: string,
    ): Promise<TableDataAndJournalParams> {
        if (form.key !== 'RECON-PF-KF') {
            throw new Error('error_type_filter_settingsForm')
        }
        const f = form as ReconPfKfFilterSettingsForm

        if (!f.region) throw new Error('error_input_region')
        if (f.measureUnit == null) throw new Error('error_input_measure_unit')
        if (f.budgetTypes.length === 0) throw new Error('error_select_budget')
        if (f.periodicity.value === 'DAY' && !f.date) throw new Error('error_input_date')
        if (f.periodicity.value === 'MONTH' && !f.month) throw new Error('error_input_month')

        const params = this.constructDataRequest(form, userId)

        const response = await fetch('/api-py/monitoring/reports-constructor/recon-pf-kf', {
            method: 'POST',
            cache: 'no-cache',
            body: JSON.stringify(params)
        })
        if (!response.ok) {
            throw new Error('error_api_report_data_failed')
        }
        const tableData = await response.json()

        const settings: FilterSettings = this.formToSettings(cloneDeep(form))

        const journalParams: any = {
            reportName: 'RECON-PF-KF',
            name: templateName,
            excel_name: excelName,
            filter_settings: settings,
            user_id: userId,
            region: f.region.value,
            region_label: i18nService.locale === 'kk' ? ((f.region as any).labelKk || f.region.label) : f.region.label,
            report_date: params.date,
            measure_unit: f.measureUnit!.value,
            budget_types: f.budgetTypes.map(it => it.value),
            rows: tableData,
            type_pf: f.typePF.value,
            type_pf_component: f.typePFComponent.value,
            expenses: f.expenses,
            gu: f.gu.map(it => i18nService.locale === 'kk' ? ((it as any).labelKk || it.label) : it.label),
            periodicity: f.periodicity.value,
            only_discrepancies: f.onlyDiscrepancies,
            only_loaded_304: f.onlyLoaded304,
            include_earlier_loaded_304: f.includeEarlierLoaded304,
            lang: i18nService.locale,
            report: ''
        }

        return {
            tableData: cloneDeep(tableData),
            journalParams
        }
    }

    public constructDataRequest(form: FilterSettingsForm, userId: string): ReportReconPfKfTableDataRequest {
        if (form.key !== 'RECON-PF-KF') {
            throw new Error('error_type_filter_settingsForm')
        }
        const f = form as ReconPfKfFilterSettingsForm

        let dateStr = ''
        let month: number | null = null

        if (f.periodicity.value === 'DAY' && f.date) {
            dateStr = moment(f.date).format('YYYY-MM-DD')
        } else if (f.periodicity.value === 'MONTH' && f.month) {
            month = f.month.value
            dateStr = `${f.year}-${String(month).padStart(2, '0')}-01`
        }

        const roundUpTo = f.roundUp ? (f.roundUpTo ?? null) : null

        const tempExpenses = f.expenses
            .map(it => ({ key: it.key, selected: it.selected, active: it.active }))

        const settings: FilterSettings = this.formToSettings(cloneDeep(form))

        const params: ReportReconPfKfTableDataRequest = {
            user_id: userId,
            date: dateStr,
            year: f.year,
            month,
            region: f.region!.value,
            budget_types: f.budgetTypes.map(it => it.value),
            type_pf: f.typePF.value,
            type_pf_component: f.typePFComponent.value,
            segmentations: f.segmentations.filter(it => it.active).map(it => it.value),
            gu: f.gu.map(it => it.value),
            vzk: f.mutuallyRedeeming!.value,
            mutually_redeeming_levels: f.mutuallyRedeemingLevels.map(it => it.value),
            statuses: f.statuses.map(it => it.value),
            measure_unit: f.measureUnit!.value,
            round_to: roundUpTo,
            expenses: tempExpenses,
            only_discrepancies: f.onlyDiscrepancies,
            only_loaded_304: f.onlyLoaded304,
            include_earlier_loaded_304: f.includeEarlierLoaded304,
            lang: i18nService.locale,
            filter_settings: settings
        }

        return params
    }
}
